import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { OrchestratorProvider } from "./context/TourOrchestrator";
import { FeaturesProvider } from "./context/FeaturesContext";
import ColetaInteligencia from "./inteligencia/ColetaInteligencia";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import PromoPopup from "./components/PromoPopup";
import NotifOnboardingGate from "./components/NotifOnboarding";
import useRouteSeo from "./seo/useRouteSeo";

// Public Pages
import HomePage from "./pages/HomePage";
import DealPage from "./pages/DealPage";
import StorePage from "./pages/StorePage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import SearchPage from "./pages/SearchPage";
import CouponsPage from "./pages/CouponsPage";
import CategoriesPage from "./pages/CategoriesPage";
import ProfilePage from "./pages/ProfilePage";
import CreateDealPage from "./pages/CreateDealPage";
import AdminPage from "./pages/AdminPage";
import PricingPage from "./pages/PricingPage";
import SobrePage from "./pages/SobrePage";
import PrivacidadePage from "./pages/PrivacidadePage";
import TermosPage from "./pages/TermosPage";
import ExclusaoDeDadosPage from "./pages/ExclusaoDeDadosPage";

// System Layout + Pages
import SystemLayout from "./components/system/SystemLayout";
import DashboardPage from "./pages/system/DashboardPage";
import CampaignsPage from "./pages/system/CampaignsPage";
import ConnectionsPage from "./pages/system/ConnectionsPage";
import AutomationPage from "./pages/system/AutomationPage";
import ManagementPage from "./pages/system/ManagementPage";
import ReportsPage from "./pages/system/ReportsPage";
import TemplatesPage from "./pages/system/TemplatesPage";
import ResearchPage from "./pages/system/ResearchPage";
import UsersPage from "./pages/system/UsersPage";
import AccountPage from "./pages/system/AccountPage";
import SettingsPage from "./pages/system/SettingsPage";
import SupportPage from "./pages/system/SupportPage";
import SocialLoginSettings from "./pages/system/SocialLoginSettings";
import AdManagerPage from "./pages/system/AdManagerPage";
import PromotionsPage from "./pages/system/PromotionsPage";
import PontoPage from "./pages/system/PontoPage";
import FuncionariosPage from "./pages/system/FuncionariosPage";
import AlertsPage from "./pages/system/AlertsPage";
import CopilotPage from "./pages/system/CopilotPage";
import OfferRequestsPage from "./pages/system/OfferRequestsPage";
import ComissoesPage from "./pages/system/ComissoesPage";
import CommunityPublishPage from "./pages/system/CommunityPublishPage";
import DiscoveriesPage from "./pages/system/DiscoveriesPage";
import CoworkOfertasPage from "./pages/system/CoworkOfertasPage";
import MlAfiliadosPage from "./pages/system/MlAfiliadosPage";
import AmazonManualPage from "./pages/system/AmazonManualPage";

// Empresa Pages
import EmpresaDashboardPage from "./pages/system/EmpresaDashboardPage";
import EmpresaGestaoPage from "./pages/system/EmpresaGestaoPage";
import EmpresaPerformancePage from "./pages/system/EmpresaPerformancePage";
import EmpresaComparativoPage from "./pages/system/EmpresaComparativoPage";
import EmpresaColaboradoresPage from "./pages/system/EmpresaColaboradoresPage";
import EmpresaUnidadesPage from "./pages/system/EmpresaUnidadesPage";
import EmpresaEscalasPage from "./pages/system/EmpresaEscalasPage";
import EmpresaPermissoesPage from "./pages/system/EmpresaPermissoesPage";
import EmpresaConfigPage from "./pages/system/EmpresaConfigPage";
import EmpresaAssinaturaPage from "./pages/system/EmpresaAssinaturaPage";
import EmpresaSolicitacoesPage from "./pages/system/EmpresaSolicitacoesPage";

// Empresa Financeiro
import EmpresaFinanceiroPage from "./pages/system/EmpresaFinanceiroPage";

// Admin Pages
import AdminEmpresasPage from "./pages/system/AdminEmpresasPage";
import AdminIndicacoesPage from "./pages/system/AdminIndicacoesPage";

// Admin Analytics
import AdminAnalyticsPage from "./pages/system/AdminAnalyticsPage";

// Admin CRM
import AdminCRMPage from "./pages/system/AdminCRMPage";

// Empresa Indicacoes
import EmpresaIndicacoesPage from "./pages/system/EmpresaIndicacoesPage";

// Empresa Alertas Historico
import EmpresaAlertasHistoricoPage from "./pages/system/EmpresaAlertasHistoricoPage";

// Empresa Ranking
import EmpresaRankingPage from "./pages/system/EmpresaRankingPage";

// Empresa WhatsApp Config
import EmpresaWhatsAppConfigPage from "./pages/system/EmpresaWhatsAppConfigPage";

// Open Finance
import OpenFinancePage from "./pages/system/OpenFinancePage";

// Redirect Campaigns
import RedirectCampaignsPage from "./pages/system/RedirectCampaignsPage";
import RedirectLandingPage from "./pages/RedirectLandingPage";
import AdminMasterPage from "./pages/system/AdminMasterPage";
import WhatsAppSignupPage from "./pages/system/WhatsAppSignupPage";
import MarketplacesPage from "./pages/system/MarketplacesPage";
import AppsPage from "./pages/system/AppsPage";
import BaixarAppPage from "./pages/BaixarAppPage";
import InviteOnboardingPage from "./pages/InviteOnboardingPage";

// Colaborador Pages
import ColaboradorDashboardPage from "./pages/system/ColaboradorDashboardPage";
import MeuDiaPage from "./pages/system/MeuDiaPage";
import MeuHistoricoPage from "./pages/system/MeuHistoricoPage";
import ColaboradorSolicitacoesPage from "./pages/system/ColaboradorSolicitacoesPage";

import { SiteTour } from "./components/Tour";
import RolagemAoNavegar from "./components/RolagemAoNavegar";

function AppRouter() {
    const location = useLocation();
    useRouteSeo(location);
    const ehRotaSistema = location.pathname.startsWith('/sistema');

    // Identidade visual do site público. Os tokens em index.css só valem sob
    // `html.sv-vitrine`, e o /sistema segue com a identidade anterior nesta fase.
    // Fica ANTES de qualquer return: hook não pode ser condicional.
    useEffect(() => {
        document.documentElement.classList.toggle('sv-vitrine', !ehRotaSistema);
    }, [ehRotaSistema]);
    
    // Handle OAuth callback (Google returns ?code= in query params)
    if (location.pathname === '/auth/callback' && location.search?.includes('code=')) {
        return <AuthCallback />;
    }

    // System routes don't show public navbar/footer
    const isSystemRoute = location.pathname.startsWith('/sistema');
    const isRedirectRoute = location.pathname.startsWith('/r/');
    const isInviteRoute = location.pathname.startsWith('/convite');
    const hideLayout = ['/login', '/auth/callback', '/baixar-app', '/downloads'].includes(location.pathname) || isSystemRoute || isRedirectRoute || isInviteRoute;


    return (
        <>
            <RolagemAoNavegar />
            {!hideLayout && <Navbar />}
            {!hideLayout && <SiteTour />}
            {!hideLayout && <PromoPopup />}
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/oferta/:dealId" element={<DealPage />} />
                <Route path="/loja/:slug" element={<StorePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/baixar-app" element={<BaixarAppPage />} />
                <Route path="/downloads" element={<BaixarAppPage />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/busca" element={<SearchPage />} />
                <Route path="/cupons" element={<CouponsPage />} />
                <Route path="/categorias" element={<CategoriesPage />} />
                <Route path="/perfil" element={<ProfilePage />} />
                <Route path="/criar-oferta" element={<CreateDealPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/planos" element={<PricingPage />} />
                <Route path="/precos" element={<PricingPage />} />
                <Route path="/sobre" element={<SobrePage />} />
                <Route path="/privacidade" element={<PrivacidadePage />} />
                <Route path="/termos" element={<TermosPage />} />
                <Route path="/exclusao-de-dados" element={<ExclusaoDeDadosPage />} />
                <Route path="/r/:slug" element={<RedirectLandingPage />} />
                <Route path="/convite/:token" element={<InviteOnboardingPage />} />

                {/* System Routes */}
                <Route path="/sistema" element={<SystemLayout />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="campanhas" element={<CampaignsPage />} />
                    <Route path="conexoes" element={<ConnectionsPage />} />
                    <Route path="connections" element={<Navigate to="/sistema/conexoes" replace />} />
                    <Route path="automacoes" element={<AutomationPage />} />
                    <Route path="automation" element={<Navigate to="/sistema/automacoes" replace />} />
                    <Route path="gestao" element={<ManagementPage />} />
                    <Route path="performance" element={<ReportsPage />} />
                    <Route path="reports" element={<Navigate to="/sistema/performance" replace />} />
                    <Route path="relatorios" element={<Navigate to="/sistema/performance" replace />} />
                    <Route path="templates" element={<TemplatesPage />} />
                    <Route path="pesquisas" element={<ResearchPage />} />
                    <Route path="usuarios" element={<UsersPage />} />
                    <Route path="users" element={<Navigate to="/sistema/usuarios" replace />} />
                    <Route path="conta" element={<AccountPage />} />
                    <Route path="account" element={<Navigate to="/sistema/conta" replace />} />
                    <Route path="configuracoes" element={<SettingsPage />} />
                    <Route path="login-social" element={<SocialLoginSettings />} />
                    <Route path="anuncios" element={<AdManagerPage />} />
                    <Route path="promocoes" element={<PromotionsPage />} />
                    <Route path="ponto" element={<PontoPage />} />
                    <Route path="funcionarios" element={<FuncionariosPage />} />
                    <Route path="settings" element={<Navigate to="/sistema/configuracoes" replace />} />
                    <Route path="suporte" element={<SupportPage />} />
                    <Route path="support" element={<Navigate to="/sistema/suporte" replace />} />
                    <Route path="alertas" element={<AlertsPage />} />
                    <Route path="copilot" element={<CopilotPage />} />
                    <Route path="pedidos" element={<OfferRequestsPage />} />
                    <Route path="comissoes" element={<ComissoesPage />} />
                    <Route path="comunidades" element={<CommunityPublishPage />} />
                    <Route path="descobertas" element={<DiscoveriesPage />} />
                    <Route path="cowork-ofertas" element={<CoworkOfertasPage />} />
                    <Route path="ml-afiliados" element={<MlAfiliadosPage />} />
                    <Route path="amazon-manual" element={<AmazonManualPage />} />
                    {/* Empresa Routes */}
                    <Route path="empresa" element={<EmpresaDashboardPage />} />
                    <Route path="empresa/gestao" element={<EmpresaGestaoPage />} />
                    <Route path="empresa/performance" element={<EmpresaPerformancePage />} />
                    <Route path="empresa/comparativo" element={<EmpresaComparativoPage />} />
                    <Route path="empresa/colaboradores" element={<EmpresaColaboradoresPage />} />
                    <Route path="empresa/unidades" element={<EmpresaUnidadesPage />} />
                    <Route path="empresa/escalas" element={<EmpresaEscalasPage />} />
                    <Route path="empresa/permissoes" element={<EmpresaPermissoesPage />} />
                    <Route path="empresa/configuracoes" element={<EmpresaConfigPage />} />
                    <Route path="empresa/assinatura" element={<EmpresaAssinaturaPage />} />
                    <Route path="empresa/solicitacoes" element={<EmpresaSolicitacoesPage />} />
                    <Route path="empresa/financeiro" element={<EmpresaFinanceiroPage />} />
                    <Route path="admin/empresas" element={<AdminEmpresasPage />} />
                    <Route path="admin/indicacoes" element={<AdminIndicacoesPage />} />
                    <Route path="admin/analytics" element={<AdminAnalyticsPage />} />
                    <Route path="admin/redirects" element={<RedirectCampaignsPage />} />
                    <Route path="admin/crm" element={<AdminCRMPage />} />
                    <Route path="admin/master" element={<AdminMasterPage />} />
                    <Route path="admin/whatsapp-signup" element={<WhatsAppSignupPage />} />
                    <Route path="marketplaces" element={<MarketplacesPage />} />
                    <Route path="aplicativos" element={<AppsPage />} />
                    <Route path="empresa/indicacoes" element={<EmpresaIndicacoesPage />} />
                    <Route path="empresa/alertas-historico" element={<EmpresaAlertasHistoricoPage />} />
                    <Route path="empresa/ranking" element={<EmpresaRankingPage />} />
                    <Route path="empresa/whatsapp" element={<EmpresaWhatsAppConfigPage />} />
                    <Route path="empresa/open-finance" element={<OpenFinancePage />} />
                    {/* Colaborador Routes */}
                    <Route path="colaborador" element={<ColaboradorDashboardPage />} />
                    <Route path="colaborador/meu-dia" element={<MeuDiaPage />} />
                    <Route path="colaborador/historico" element={<MeuHistoricoPage />} />
                    <Route path="colaborador/solicitacoes" element={<ColaboradorSolicitacoesPage />} />
                    {/* Catch-all: redirect unknown /sistema/* paths to dashboard */}
                    <Route path="*" element={<Navigate to="/sistema" replace />} />
                </Route>
                {/* Catch-all: redirect unknown top-level paths to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {!hideLayout && <Footer />}
        </>
    );
}

// SplashScreen do React removida a pedido: o app abria com uma logo grande de 2.2s.
// Agora abre direto (a casca nativa do TWA/Electron ja mostra um splash rapido do icone).

// Global Error Boundary — catches unhandled render crashes
class GlobalErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(error, info) { console.error('[GlobalErrorBoundary]', error, info); }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#071A3A', color: '#A8B6CC', fontFamily: 'system-ui', padding: 20 }}>
                    <div style={{ textAlign: 'center', maxWidth: 400 }}>
                        <div style={{ fontSize: 32, marginBottom: 12 }}>!</div>
                        <p style={{ fontSize: 14, color: 'var(--sv-danger)', fontWeight: 600 }}>Erro inesperado</p>
                        <p style={{ fontSize: 12, marginTop: 8, color: '#888' }}>{String(this.state.error?.message || '').slice(0, 200)}</p>
                        <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/sistema'; }} style={{ marginTop: 16, padding: '8px 16px', background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Voltar ao Dashboard
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function App() {
    return (
        <GlobalErrorBoundary>
        <ThemeProvider>
            <div className="min-h-screen bg-background theme-transition">
                <BrowserRouter>
                    <AuthProvider>
                        <FeaturesProvider>
                        <ColetaInteligencia />
                        <OrchestratorProvider>
                            <AppRouter />
                            <NotifOnboardingGate />
                            <Toaster
                                position="top-right" 
                                richColors 
                                toastOptions={{
                                    className: 'bg-card border-border text-foreground'
                                }}
                            />
                        </OrchestratorProvider>
                        </FeaturesProvider>
                    </AuthProvider>
                </BrowserRouter>
            </div>
        </ThemeProvider>
        </GlobalErrorBoundary>
    );
}

export default App;
